import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { enqueueSnackbar } from 'notistack'
import { type ComponentProps, type ReactNode, useEffect, useEffectEvent, useRef, useState } from 'react'
import { type DefaultValues, type FieldPath, FormProvider, get, useForm, useFormState } from 'react-hook-form'
import type { z } from 'zod'
import { I18n } from '../../../components/I18n.tsx'
import { FormLayout, FormPage } from '../../../components/Layouts.tsx'
import { TitleSubtitle } from '../../../components/TitleSubtitle.tsx'
import { CreateAnalyzeReportError } from '../../../data/analyze/reports.ts'
import type { AssetLocation } from '../../../data/locations.tsx'
import type { I18nKey } from '../../../i18n/i18n.ts'
import { ids } from '../../../ids.ts'
import { useNavigateBack } from '../../../navigation.ts'
import { CreateReportFormMap } from './CreateReportFormMap.tsx'
import type { ReportType, ReportTypeSelection } from './reportTypes.tsx'
import { type BaseReportForm, useBaseFormValues } from './schema.ts'
import { selectReportTypeStep } from './steps/selectReportType.tsx'

export type FormFieldPath<TForm extends BaseReportForm> = FieldPath<BaseReportForm> | FieldPath<TForm>

export type ReportFormStep<TForm extends BaseReportForm = BaseReportForm> = {
	id: string
	label: ReactNode
	shortDescription: ReactNode
	longDescription: ReactNode
	// The fields of the step; the shared page chrome is around them already
	form: ReactNode
	header?: ReactNode
	fieldsToValidate: readonly FormFieldPath<TForm>[]
	mapInteractive?: boolean
	disableNext?: boolean
}

export type ReportFormProps = {
	locations: AssetLocation[]
	selectedLocations: AssetLocation[]
	onLocationsSelected: (locations: AssetLocation[]) => void
}

export type ReportTypeConfig<TForm extends BaseReportForm = BaseReportForm> = {
	title: I18nKey
	schema: z.ZodType<TForm, TForm>
	defaultValues: DefaultValues<TForm>
	steps: ReportFormStep<TForm>[]
	onSubmit: (data: TForm) => Promise<unknown>
	mapLayers?: ReactNode
	maxAssets: number
}

// Type-checks the config against the report type's own form shape, then erases it to the base form the shell drives.
export const defineReportType = <TForm extends BaseReportForm>(config: ReportTypeConfig<TForm>): ReportTypeConfig =>
	config as unknown as ReportTypeConfig

// One form serves every report type, so that switching type does not remount the form or the map.
export const CreateReportForm = ({
	reportType,
	config: { title, schema, defaultValues, steps: reportSteps, onSubmit, mapLayers, maxAssets },
	locations,
	selectedLocations,
	onLocationsSelected,
	reportTypeSelection,
}: ReportFormProps & {
	reportType: ReportType
	config: ReportTypeConfig
	// Adds a report type step in front of the form; set when more than one type is available.
	reportTypeSelection?: ReportTypeSelection
}) => {
	const navigate = useNavigate()
	const navigateBack = useNavigateBack()
	const [activeStep, setActiveStep] = useState(0)
	// `useForm` re-reads its options on every render, so the resolver follows the chosen report type
	const form = useForm<BaseReportForm>({ resolver: zodResolver(schema), mode: 'onBlur', defaultValues })
	const { isSubmitting } = useFormState({ control: form.control })

	// Not on mount: `useForm` starts from the defaults already, and a reset would clear what the steps set as they mount
	const formReportType = useRef(reportType)
	const startFromDefaults = useEffectEvent(() => form.reset(defaultValues))
	useEffect(() => {
		if (formReportType.current === reportType) return
		formReportType.current = reportType
		startFromDefaults()
	}, [reportType])

	const steps = reportTypeSelection ? [selectReportTypeStep(reportTypeSelection), ...reportSteps] : reportSteps
	const step = steps[activeStep]

	const submit = form.handleSubmit(
		async (data) => {
			try {
				await onSubmit(data)
			} catch (error) {
				const detail = error instanceof CreateAnalyzeReportError ? error.detail : undefined
				enqueueSnackbar({
					variant: 'error',
					message: (
						<>
							<I18n i18nKey={($) => $.reports.form.failedToCreateReport} />
							{detail && (
								<>
									<br />
									<br />
									{detail}
								</>
							)}
						</>
					),
				})
				return
			}
			enqueueSnackbar({ variant: 'success', message: <I18n i18nKey={($) => $.reports.form.succeededToCreateReport} /> })
			void navigate({ to: '/reports' })
		},
		(errors) => {
			// A step can become invalid after the user has moved past it, e.g. when the data availability updates
			const firstInvalidStep = steps.findIndex((step) => step.fieldsToValidate.some((field) => get(errors, field)))
			if (firstInvalidStep >= 0) setActiveStep(firstInvalidStep)
		},
	)

	const validateStep = async () => {
		if (!step || step.fieldsToValidate.length === 0) return true
		return form.trigger(step.fieldsToValidate)
	}

	return (
		<FormProvider {...form}>
			<FormLayoutWaitingForGeometry
				waitsForGeometry={!!step?.fieldsToValidate.includes('geometryPending')}
				onBack={() => navigateBack({ fallback: '/reports' })}
				title={title}
				activeStep={activeStep}
				steps={steps.map(({ id, label, shortDescription }) => ({ id, label, description: shortDescription }))}
				isSubmitting={isSubmitting}
				disableNext={step?.disableNext}
				showStepLabelIndex
				stepsControl={{
					onBack: () => setActiveStep(activeStep - 1),
					onNext: async () => {
						if (await validateStep()) {
							setActiveStep(activeStep + 1)
						}
					},
					onSave: submit,
					onCancel: () => navigateBack({ fallback: '/reports' }),
				}}
			>
				<FormLayout.Split
					left={
						<FormPage dataTa={ids.reports.form.root} headerContent={step?.header}>
							{step && (
								<FormPage.Content>
									<TitleSubtitle title={step.label} subtitle={step.longDescription} />
									{step.form}
								</FormPage.Content>
							)}
						</FormPage>
					}
					right={
						<CreateReportFormMap
							interactive={!!step?.mapInteractive}
							maxAssets={maxAssets}
							locations={locations}
							selectedLocations={selectedLocations}
							onLocationsSelected={onLocationsSelected}
						>
							{mapLayers}
						</CreateReportFormMap>
					}
				/>
			</FormLayoutWaitingForGeometry>
		</FormProvider>
	)
}

// Its own component, so that only the layout renders when the outline is pending or ready
const FormLayoutWaitingForGeometry = ({
	waitsForGeometry,
	disableNext,
	...props
}: ComponentProps<typeof FormLayout> & { waitsForGeometry: boolean }) => {
	const [geometryPending] = useBaseFormValues(['geometryPending'])
	return <FormLayout {...props} disableNext={disableNext || (waitsForGeometry && geometryPending)} />
}
